import { C, MONO, hexA } from '../lib/tokens';
import { fmtAge } from '../lib/format';
import {
  deriveTradingVpsState,
  groupTradingVps,
  summarizeTradingVps,
  tradingVpsStaleAgeS,
  type TradingVpsStateKey,
} from '../lib/tradingVps';
import { useData, useTradingVpsDataAgeS } from '../store/hooks';
import { navGo } from '../store/uiStore';

function stateColor(key: TradingVpsStateKey): string {
  if (key === 'live') return C.gn;
  if (key === 'delayed' || key === 'algo_off') return C.am;
  return C.rd;
}

export function TradingVpsHealthBand() {
  const rows = useData((state) => state.tradingVps);
  const error = useData((state) => state.tradingVpsError);
  const dataAgeS = useTradingVpsDataAgeS();
  const staleAgeS = tradingVpsStaleAgeS(dataAgeS);
  const summary = summarizeTradingVps(rows, staleAgeS);
  const ordered = groupTradingVps(rows, staleAgeS);
  const fleetColor = summary.issues ? C.rd : summary.total ? C.gn : C.gy;
  const degraded = error != null || staleAgeS > 0;
  const note = error != null
    ? 'Last update failed · showing last known state'
    : staleAgeS > 0
      ? `Feed stale · last update ${fmtAge(dataAgeS * 1000)} ago`
      : summary.message;

  return (
    <div
      aria-label="Trading VPS fleet health"
      style={{
        display: 'flex', alignItems: 'stretch', flexWrap: 'wrap',
        background: C.s1, border: '1px solid ' + C.ln, minHeight: 66,
      }}
    >
      <div style={{ padding: '10px 14px', minWidth: 205, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
        <span style={{ color: C.dm, fontSize: 10.5, fontFamily: MONO }}>EXECUTION HOSTS</span>
        <span style={{ color: fleetColor, fontSize: 18, fontFamily: MONO, fontWeight: 600, marginTop: 3 }}>
          {summary.live} / {summary.total} live
        </span>
        <span style={{ color: degraded ? C.am : C.dm, fontSize: 10.5, marginTop: 2 }}>
          {note}
        </span>
      </div>

      <div style={{ flex: '1 1 470px', display: 'flex', alignItems: 'stretch', flexWrap: 'wrap', borderLeft: '1px solid ' + C.ln2 }}>
        {ordered.map((row) => {
          const state = deriveTradingVpsState(row, staleAgeS);
          const color = stateColor(state.key);
          return (
            <div
              key={row.vps_id}
              data-testid="trading-vps-marker"
              aria-label={`${row.vps_id} ${state.label}`}
              title={`${row.account_label ?? 'Unmatched account'} · ${state.label}`}
              style={{
                width: 104, minWidth: 104, height: 64, padding: '9px 10px', boxSizing: 'border-box',
                borderRight: '1px solid ' + C.ln2, borderBottom: '1px solid ' + C.ln2,
                background: state.issue ? hexA(color, 0.055) : 'transparent',
                display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 5,
              }}
            >
              <span style={{ color: C.b9, fontFamily: MONO, fontSize: 10.5 }}>{row.vps_id.replace('TRADE-VPS-', 'VPS ')}</span>
              <span style={{ color, fontFamily: MONO, fontSize: 10, fontWeight: 600, whiteSpace: 'nowrap' }}>
                {state.issue ? '✕' : '●'} {state.label}
              </span>
            </div>
          );
        })}
        {ordered.length === 0 && (
          <div style={{ color: C.dm, fontSize: 11.5, padding: '22px 14px' }}>No direct guards reporting.</div>
        )}
      </div>

      <button
        type="button"
        onClick={() => navGo('tradingVps')}
        style={{
          minWidth: 128, padding: '0 14px', background: 'transparent', border: 'none', borderLeft: '1px solid ' + C.ln,
          color: C.bl, fontSize: 11.5, fontWeight: 600, textAlign: 'center',
        }}
      >
        View trading VPS
      </button>
    </div>
  );
}
